/**
 * Stacking time-ranged tags into lanes.
 *
 * Every effect that occupies a stretch of the timeline — captions today,
 * speed ramps and freezes next — needs the same thing: tags that don't
 * overlap visually, stacked downwards as they pile up, the way an NLE
 * stacks tracks. This is that packing, kept pure so the rule is one
 * function instead of one per effect type.
 */

/** Anything with a span on the project timebase. */
export interface Spanned {
  id: string
  start: number
  end: number
}

export interface LanedItem<T extends Spanned> {
  item: T
  /** 0-based row, counting downwards. */
  lane: number
}

export interface LaneLayout<T extends Spanned> {
  items: LanedItem<T>[]
  /** How many rows the layout needs — 0 when there's nothing. */
  laneCount: number
}

/**
 * Assign each item the topmost lane where it doesn't overlap.
 *
 * Greedy over items sorted by start, which is optimal for interval graphs:
 * it never uses more lanes than the maximum number of items overlapping at
 * any instant, and that is the true lower bound.
 *
 * ``gap`` keeps neighbouring tags from touching, so two adjacent captions
 * read as two and not as one long bar. It's in seconds because it's
 * compared against the timebase — the caller converts from pixels if it
 * wants a visual constant.
 */
export function packIntoLanes<T extends Spanned>(items: T[], gap = 0): LaneLayout<T> {
  // Ties broken by id so the layout doesn't reshuffle when two tags share a
  // start — a lane that jumps around under the cursor is unusable.
  const sorted = [...items].sort((a, b) => a.start - b.start || a.id.localeCompare(b.id))

  /** Where each lane is currently free from. */
  const laneEnds: number[] = []
  const placed: LanedItem<T>[] = []

  for (const item of sorted) {
    let lane = laneEnds.findIndex((end) => end <= item.start - gap)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(item.end)
    } else {
      laneEnds[lane] = item.end
    }
    placed.push({ item, lane })
  }

  return { items: placed, laneCount: laneEnds.length }
}
